/**
 * Catálogo único de endpoints ArcGIS MIDAGRI que consume el visor: árbol de
 * capas (TOC) y cruce de exposición del KPI. Centralizar servicio+subcapa en
 * un solo lugar evita que una URL duplicada entre repositorio y KPI diverja
 * cuando un servicio se muda o cae.
 *
 * Estado verificado en vivo el 2026-10-16:
 *  - geoservicios (ArcGIS 11.5): operativo. SAC/SAC sigue sirviendo los sectores
 *    estadísticos; los límites políticos (departamento/provincia/distrito) pasaron
 *    a SERV_LIMITE_CENSAL en winlmprap14 (códigos INEI 2/4/6: ver LIMITE_FIELDS).
 *  - winjmprap12/ : OBSRV_INFOBASE y OBSRV_INFOCORES_DATA detenidos
 *    ("not started"); requieren que infraestructura los reinicie.
 *  - temporal2 (padrones de productores): operativo y rápido.
 *  - OJO con la ortografía de los hosts: "winjmprap12" y "winlmprap14" son
 *    rutas DISTINTAS. Usar siempre estas constantes, nunca URLs literales.
 *  - ANA/SNIRH: `PC_Priorizados_2026` vive en snirhgeo (nuevo host); SSDH/ONRH/
 *    Público siguen en geosnirh. Verificados en vivo el 2026-09-26.
 */
export class ArcgisEndpointConstant {
  private static readonly GEOSERVICIOS = 'https://geoservicios.midagri.gob.pe/server/rest/services';
  private static readonly WINJMPRAP12 = 'https://winlmprap09.midagri.gob.pe/winjmprap12/rest/services';
  /** `winlmprap14` (mismo host, ruta distinta): límites censales y padrones temporal2. */
  private static readonly WINJMPRAP14 = 'https://winlmprap09.midagri.gob.pe/winlmprap14/rest/services';
  // ── SERFOR — geoservicios de incendios forestales ──
  private static readonly SERFOR_GEOSERVICIOS = 'https://geo.serfor.gob.pe/geoservicios/rest/services';

  // ── ANA / SNIRH ──
  // - geosnirh.ana.gob.pe (histórico, sigue activo): SSDH (áreas inundadas), ONRH (cuerpos de agua) y OGC/WFS (Público).
  // - snirhgeo.ana.gob.pe (nuevo): catálogo `PC_Priorizados_2026` — D.U. 008-2026/MINAGRO (verificado en vivo el 2026-09-26).
  private static readonly GEOSNIRH_ANA = 'https://geosnirh.ana.gob.pe/server/rest/services';
  private static readonly SNIRHGEO_ANA = 'https://snirhgeo.ana.gob.pe/server/rest/services';
  /** Endpoint OGC (WFS) de ANA — formato `{host}/server/services/{carpeta}/{servicio}/MapServer/WFSServer`. */
  private static readonly GEOSNIRH_OGC = 'https://geosnirh.ana.gob.pe/server/services';

  static readonly SAC_MAPSERVER = `${ArcgisEndpointConstant.GEOSERVICIOS}/SAC/SAC/MapServer`;
  /**
   * Límites políticos censales (INEI): departamento 0 · provincia 1 · distrito 2.
   * Única fuente del TOC y del ubigeo; ver `LIMITE_FIELDS` para el esquema de campos.
   */
  static readonly LIMITE_CENSAL_MAPSERVER = `${ArcgisEndpointConstant.WINJMPRAP14}/SERV_LIMITE_CENSAL/MapServer`;
  static readonly PARCELA_MAPSERVER = `${ArcgisEndpointConstant.GEOSERVICIOS}/SIG_UE_PARCELA_MDE_MIL1/MapServer`;
  static readonly CENTROS_POBLADOS_FEATURESERVER = `${ArcgisEndpointConstant.GEOSERVICIOS}/MDE/MDE_20136201213/FeatureServer`;
  static readonly CATASTRAL_RURAL_MAPSERVER = `${ArcgisEndpointConstant.WINJMPRAP12}/visorPublico/Catastral_Rural_Servicio_Dev/MapServer`;
  static readonly INFOCORES_MAPSERVER = `${ArcgisEndpointConstant.WINJMPRAP12}/ /OBSRV_INFOCORES_DATA/MapServer`;
  static readonly INFOBASE_MAPSERVER = `${ArcgisEndpointConstant.WINJMPRAP12}/ /OBSRV_INFOBASE/MapServer`;
  static readonly INFOCORES_TEMPORAL2_MAPSERVER = `${ArcgisEndpointConstant.WINJMPRAP14}/temporal2/OBSRV_INFOCORES_DATA/MapServer`;

  // ── SERFOR — `Incendios_Forestales` (puntos: capa 1 "Incendio Forestal",
  //    capa 0 "Focos de Calor (últimas 24 horas)"). Verificado en vivo el 2026-10-14. ──
  static readonly INCENDIOS_FORESTALES_MAPSERVER =
    `${ArcgisEndpointConstant.SERFOR_GEOSERVICIOS}/Visor/Incendios_Forestales/MapServer`;

  // ── ANA / SNIRH — servicio `PC_Priorizados_2026` (geosnirh / snirhgeo) ──
  static readonly PC_PRIORIZADOS_2026 = `${ArcgisEndpointConstant.SNIRHGEO_ANA}/PC_Priorizados_2026`;
  static readonly PC_DU_2026 = `${ArcgisEndpointConstant.GEOSNIRH_ANA}/PC_DU_2026`;
  static readonly PC_PUNTOS_CRITICOS_INICIAL_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_DU_2026}/Puntos_Criticos_Inicial_107_OK_Nuevos_Propuestos/FeatureServer`;
  static readonly PC_PUNTOS_CRITICOS_FINAL_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Puntos_Criticos_Final_Priorizados_2026_Automatizado/FeatureServer`;
  static readonly PC_PUNTOS_CRITICOS_TRAMOS_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Puntos_Criticos_Tramos_Priorizados_2026_Automatizado/FeatureServer`;
  static readonly PC_BARRERAS_DINAMICAS_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Barreras_Dinamicas_Priorizadas_DU0082026/FeatureServer`;
  static readonly PC_ULTIMA_POSICION_MAQUINARIA_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Ultima_Posicion_Maquinaria_PC_Priorizados_2026/FeatureServer`;
  static readonly PC_RIOS_QUEBRADAS_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Rios_Quebradas_ANA_PC_Priorizados_2026/FeatureServer`;
  static readonly PC_UNIDADES_HIDROGRAFICAS_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Unidades_Hidrograficas_PC_Priorizados_2026/FeatureServer`;
  static readonly PC_DEPARTAMENTOS_EMERGENCIA_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Departamentos_Estado_Emergencia_PC_Priorizados_2026/FeatureServer`;
  // Contexto de trazabilidad (no se publican en el TOC: duplican la cartografía base MIDAGRI).
  static readonly PC_LIMITE_DEPARTAMENTAL_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Limite_Departamental_PC_Priorizados_2026/FeatureServer`;
  static readonly PC_LIMITE_PROVINCIAL_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Limite_Provincial_PC_Priorizados_2026/FeatureServer`;
  static readonly PC_LIMITE_DISTRITAL_2026_FEATURESERVER =
    `${ArcgisEndpointConstant.PC_PRIORIZADOS_2026}/Limite_Distrital_PC_Priorizados_2026/FeatureServer`;

  // ── ANA / SNIRH — servicios históricos que permanecen en geosnirh ──
  static readonly SSDH = `${ArcgisEndpointConstant.GEOSNIRH_ANA}/SSDH`;
  static readonly AREAS_INUNDADAS_PIURA_12022017_MAPSERVER =
    `${ArcgisEndpointConstant.SSDH}/Areas_Inundadas_Piura_12022017_DCP/MapServer`;
  static readonly AREAS_INUNDADAS_PIURA_19012017_MAPSERVER =
    `${ArcgisEndpointConstant.SSDH}/Areas_Inundadas_Piura_19012017_DCP/MapServer`;
  static readonly AREAS_INUNDADAS_PIURA_20032017_MAPSERVER =
    `${ArcgisEndpointConstant.SSDH}/Areas_Inundadas_Piura_20032017_DCP/MapServer`;
  static readonly AREAS_INUNDADAS_PIURA_23032017_MAPSERVER =
    `${ArcgisEndpointConstant.SSDH}/Areas_Inundadas_Piura_23032017_DCP/MapServer`;
  static readonly AREAS_INUNDADAS_TUMBES_15042023_MAPSERVER =
    `${ArcgisEndpointConstant.SSDH}/Areas_Inundadas_Tumbes_15042023_DCP/MapServer`;
  static readonly ONRH_CUERPOS_AGUA_MAPSERVER =
    `${ArcgisEndpointConstant.GEOSNIRH_ANA}/ONRH/Cuerpos_Agua_ONRH/MapServer`;
  /**
   * Hidrografía lótica (ríos). "Público" equivale a la forma percent-encoded `P%C3%BAblico`.
   * OJO: verificado en vivo el 2026-09-26 el servicio responde `not started` en ANA, por eso el
   * visor usa `PC_RIOS_QUEBRADAS_2026` (snirhgeo). Catalogado para cuando ANA lo reinicie.
   */
  static readonly ANA_AGUAS_CONTINENTALES_LOTICOS_MAPSERVER =
    `${ArcgisEndpointConstant.GEOSNIRH_ANA}/Público/SERV_AguasContiLoticos/MapServer`;

  /** Ejemplo de servicio OGC/WFS (ANA — DUA Acuícola). */
  static readonly ANA_DUA_ACUICOLA_WFS =
    `${ArcgisEndpointConstant.GEOSNIRH_OGC}/Público/DUA_Acuicola/MapServer/WFSServer`;

  // ── Referencias servicio + subcapa (única fuente del índice de subcapa) ──
  // Límites políticos: SERV_LIMITE_CENSAL (winlmprap14), en orden dep 0 · prov 1 · dist 2.
  static readonly LIMITE_DEPARTAMENTO = { baseUrl: ArcgisEndpointConstant.LIMITE_CENSAL_MAPSERVER, sublayer: 0 } as const;
  static readonly LIMITE_PROVINCIAL = { baseUrl: ArcgisEndpointConstant.LIMITE_CENSAL_MAPSERVER, sublayer: 1 } as const;
  static readonly LIMITE_DISTRITAL = { baseUrl: ArcgisEndpointConstant.LIMITE_CENSAL_MAPSERVER, sublayer: 2 } as const;
  static readonly SECTORES_ESTADISTICOS = { baseUrl: ArcgisEndpointConstant.SAC_MAPSERVER, sublayer: 6 } as const;

  /**
   * Esquema de campos de `SERV_LIMITE_CENSAL`. Se centraliza aquí porque ubigeo,
   * temperatura y cruce KPI escriben `where`/`outFields` literales: si el servicio
   * cambia de columnas, cambia un solo lugar.
   *
   * El código INEI conserva el largo jerárquico 2/4/6 con el que el visor deduce
   * el nivel territorial:
   *   · departamento → `CODDEP` (2 dígitos)
   *   · provincia    → `CODPROV` (4 dígitos = dept + prov)
   *   · distrito     → `UBIGEO`  (6 dígitos = dept + prov + dist)
   *
   * OJO en distritos: `CODDEP`/`CODPROV`/`CODDIST` son los componentes de 2 dígitos
   * (para filtrar una provincia se usa `CODDEP` + `CODPROV`, NO el `CODPROV` de 4
   * dígitos de la capa de provincias).
   */
  static readonly LIMITE_FIELDS = {
    DEPARTAMENTO: { code: 'CODDEP', name: 'NOMBDEP' },
    PROVINCIA: { code: 'CODPROV', name: 'NOMBPROV', departmentField: 'CODDEP' },
    DISTRITO: { code: 'UBIGEO', name: 'NOMBDIST', departmentField: 'CODDEP', provinceField: 'CODPROV' },
  } as const;
  static readonly CENTROS_POBLADOS = { baseUrl: ArcgisEndpointConstant.CENTROS_POBLADOS_FEATURESERVER, sublayer: 5 } as const;
  static readonly PREDIO_RURAL_CATASTRAL = { baseUrl: ArcgisEndpointConstant.CATASTRAL_RURAL_MAPSERVER, sublayer: 0 } as const;
  static readonly COMUNIDADES_CAMPESINAS = { baseUrl: ArcgisEndpointConstant.CATASTRAL_RURAL_MAPSERVER, sublayer: 1 } as const;
  static readonly COMUNIDADES_NATIVAS = { baseUrl: ArcgisEndpointConstant.CATASTRAL_RURAL_MAPSERVER, sublayer: 2 } as const;
  static readonly PARCELAS_CULTIVO = { baseUrl: ArcgisEndpointConstant.PARCELA_MAPSERVER, sublayer: 3 } as const;
  static readonly PRODUCTORES_AGRICOLAS = { baseUrl: ArcgisEndpointConstant.INFOCORES_TEMPORAL2_MAPSERVER, sublayer: 9 } as const;
  static readonly PRODUCTORES_PECUARIOS = { baseUrl: ArcgisEndpointConstant.INFOCORES_TEMPORAL2_MAPSERVER, sublayer: 11 } as const;
  static readonly CENTROS_EMPADRONADOS = { baseUrl: ArcgisEndpointConstant.INFOCORES_MAPSERVER, sublayer: 1 } as const;
  static readonly JUNTAS_USUARIO = { baseUrl: ArcgisEndpointConstant.INFOBASE_MAPSERVER, sublayer: 3 } as const;
  static readonly COMITES_RIEGO = { baseUrl: ArcgisEndpointConstant.INFOBASE_MAPSERVER, sublayer: 4 } as const;
  static readonly AGENCIAS_AGRARIAS = { baseUrl: ArcgisEndpointConstant.INFOBASE_MAPSERVER, sublayer: 8 } as const;

  // ── SERFOR — incendios forestales ──
  static readonly INCENDIO_FORESTAL = {
    baseUrl: ArcgisEndpointConstant.INCENDIOS_FORESTALES_MAPSERVER,
    sublayer: 1,
  } as const;
  static readonly FOCOS_CALOR_24H = {
    baseUrl: ArcgisEndpointConstant.INCENDIOS_FORESTALES_MAPSERVER,
    sublayer: 0,
  } as const;

  // ── ANA / SNIRH — PC_Priorizados_2026: cada servicio expone una única capa (id 0) ──
  static readonly PC_PUNTOS_CRITICOS_INICIAL_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_PUNTOS_CRITICOS_INICIAL_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_PUNTOS_CRITICOS_FINAL_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_PUNTOS_CRITICOS_FINAL_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_PUNTOS_CRITICOS_TRAMOS_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_PUNTOS_CRITICOS_TRAMOS_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_BARRERAS_DINAMICAS_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_BARRERAS_DINAMICAS_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_ULTIMA_POSICION_MAQUINARIA_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_ULTIMA_POSICION_MAQUINARIA_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_RIOS_QUEBRADAS_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_RIOS_QUEBRADAS_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_UNIDADES_HIDROGRAFICAS_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_UNIDADES_HIDROGRAFICAS_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_DEPARTAMENTOS_EMERGENCIA_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_DEPARTAMENTOS_EMERGENCIA_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_LIMITE_DEPARTAMENTAL_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_LIMITE_DEPARTAMENTAL_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_LIMITE_PROVINCIAL_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_LIMITE_PROVINCIAL_2026_FEATURESERVER,
    sublayer: 0,
  } as const;
  static readonly PC_LIMITE_DISTRITAL_2026 = {
    baseUrl: ArcgisEndpointConstant.PC_LIMITE_DISTRITAL_2026_FEATURESERVER,
    sublayer: 0,
  } as const;

  // ── ANA / SNIRH — históricos en geosnirh ──
  static readonly AREAS_INUNDADAS_PIURA_12022017 = {
    baseUrl: ArcgisEndpointConstant.AREAS_INUNDADAS_PIURA_12022017_MAPSERVER,
    sublayer: 0,
  } as const;
  static readonly AREAS_INUNDADAS_PIURA_19012017 = {
    baseUrl: ArcgisEndpointConstant.AREAS_INUNDADAS_PIURA_19012017_MAPSERVER,
    sublayer: 0,
  } as const;
  static readonly AREAS_INUNDADAS_PIURA_20032017 = {
    baseUrl: ArcgisEndpointConstant.AREAS_INUNDADAS_PIURA_20032017_MAPSERVER,
    sublayer: 0,
  } as const;
  static readonly AREAS_INUNDADAS_PIURA_23032017 = {
    baseUrl: ArcgisEndpointConstant.AREAS_INUNDADAS_PIURA_23032017_MAPSERVER,
    sublayer: 0,
  } as const;
  static readonly AREAS_INUNDADAS_TUMBES_15042023 = {
    baseUrl: ArcgisEndpointConstant.AREAS_INUNDADAS_TUMBES_15042023_MAPSERVER,
    sublayer: 0,
  } as const;
  static readonly ONRH_CUERPOS_AGUA = {
    baseUrl: ArcgisEndpointConstant.ONRH_CUERPOS_AGUA_MAPSERVER,
    sublayer: 37,
  } as const;
  static readonly ANA_AGUAS_CONTINENTALES_LOTICOS = {
    baseUrl: ArcgisEndpointConstant.ANA_AGUAS_CONTINENTALES_LOTICOS_MAPSERVER,
    sublayer: 37,
  } as const;

  /** URL completa (baseUrl + subcapa) para el árbol de capas. */
  static urlOf(ref: { readonly baseUrl: string; readonly sublayer: number }): string {
    return `${ref.baseUrl}/${ref.sublayer}`;
  }
}
